import { Injectable } from '@angular/core';
import { getMcpEndpointUrl } from '../mcp/mcp-connector.util';
import { CopilotMcpConfig } from './copilot.types';

/**
 * localStorage key holding the Time Doctor MCP api key used by Tempo for live workspace queries.
 * Stored client-side so the copilot can send it per request (stateless bridge on the agent side).
 */
export const COPILOT_MCP_KEY_STORAGE_KEY = 'tempoMcpApiKey';

/** localStorage key for the server-side MCP key id (used to revoke on disconnect). */
export const COPILOT_MCP_KEY_ID_STORAGE_KEY = 'tempoMcpApiKeyId';

/**
 * Persists the caller's Time Doctor MCP key and exposes it as a per-request config. The MCP endpoint
 * URL is derived from the current deployment so it always matches the environment that issued the key.
 */
@Injectable({ providedIn: 'root' })
export class CopilotMcpConfigService {
  getApiKey(): string | null {
    try {
      return localStorage.getItem(COPILOT_MCP_KEY_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  getKeyId(): string | null {
    try {
      return localStorage.getItem(COPILOT_MCP_KEY_ID_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  setApiKey(apiKey: string, keyId?: string): void {
    try {
      localStorage.setItem(COPILOT_MCP_KEY_STORAGE_KEY, apiKey);
      if (keyId) {
        localStorage.setItem(COPILOT_MCP_KEY_ID_STORAGE_KEY, keyId);
      }
    } catch {
      // Ignore storage failures (private mode / quota); the copilot simply stays without live data.
    }
  }

  clear(): void {
    try {
      localStorage.removeItem(COPILOT_MCP_KEY_STORAGE_KEY);
      localStorage.removeItem(COPILOT_MCP_KEY_ID_STORAGE_KEY);
    } catch {
      // Ignore storage failures.
    }
  }

  isConnected(): boolean {
    return !!this.getApiKey();
  }

  /** Per-request MCP config, or null when no key is stored. */
  getConfig(): CopilotMcpConfig | null {
    const apiKey = this.getApiKey();
    if (!apiKey) {
      return null;
    }
    return { url: getMcpEndpointUrl(), apiKey };
  }
}
